(() => {
  class BlogPostsFilter {
    constructor(root) {
      this.root = root;

      this.blogUrl = (
        root.dataset.blogUrl || ''
      ).replace(/\/$/, '');

      this.nav = root.querySelector(
        '[data-blog-filter-nav]'
      );

      this.results = root.querySelector(
        '[data-blog-results]'
      );

      this.grid = root.querySelector(
        '[data-blog-grid]'
      );

      this.pagination = root.querySelector(
        '[data-blog-pagination]'
      );

      this.controller = null;

      this.init();
    }

    init() {
      if (!this.blogUrl) {
        return;
      }

      /*
       * Tag filters
       */
      if (this.nav) {
        const buttons =
          this.nav.querySelectorAll(
            '[data-blog-tag]'
          );

        buttons.forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              this.handleTagClick(button);
            }
          );
        });
      }

      /*
       * Pagination
       */
      this.bindPagination();
    }

    bindPagination() {
      const pagination =
        this.root.querySelector(
          '[data-blog-pagination]'
        );

      if (!pagination) {
        return;
      }

      const buttons =
        pagination.querySelectorAll(
          '[data-pagination-url]'
        );

      buttons.forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const url =
              button.dataset.paginationUrl;

            if (url) {
              this.loadPage(url);
            }
          }
        );
      });
    }

    async handleTagClick(button) {
      if (
        button.classList.contains(
          'is-active'
        )
      ) {
        return;
      }

      const tag =
        button.dataset.blogTag || '';

      this.setActiveTag(button);

      /*
       * When changing tag, always go
       * back to page 1.
       */
      const url = tag
        ? `${this.blogUrl}/tagged/${encodeURIComponent(tag)}`
        : this.blogUrl;

      await this.loadPage(url);
    }

    setActiveTag(activeButton) {
      if (!this.nav) {
        return;
      }

      const buttons =
        this.nav.querySelectorAll(
          '[data-blog-tag]'
        );

      buttons.forEach((button) => {
        const isActive =
          button === activeButton;

        button.classList.toggle(
          'is-active',
          isActive
        );

        button.setAttribute(
          'aria-pressed',
          isActive
            ? 'true'
            : 'false'
        );
      });
    }

    async loadPage(url) {
      if (!this.results) {
        return;
      }

      /*
       * Cancel previous request
       */
      if (this.controller) {
        this.controller.abort();
      }

      this.controller =
        new AbortController();

      this.setLoading(true);

      try {
        const response =
          await fetch(url, {
            method: 'GET',

            headers: {
              'X-Requested-With':
                'XMLHttpRequest',

              Accept:
                'text/html',
            },

            signal:
              this.controller.signal,
          });

        if (!response.ok) {
          throw new Error(
            `Request failed: ${response.status}`
          );
        }

        const html =
          await response.text();

        const parser =
          new DOMParser();

        const document =
          parser.parseFromString(
            html,
            'text/html'
          );

        /*
         * Find our section in
         * returned HTML.
         */
        const newSection =
          document.querySelector(
            '[data-blog-posts-filter]'
          );

        if (!newSection) {
          throw new Error(
            'Blog posts filter section not found.'
          );
        }

        /*
         * New results
         */
        const newResults =
          newSection.querySelector(
            '[data-blog-results]'
          );

        if (!newResults) {
          throw new Error(
            'Blog results not found.'
          );
        }

        /*
         * New grid
         */
        const newGrid =
          newResults.querySelector(
            '[data-blog-grid]'
          );

        if (!newGrid) {
          throw new Error(
            'Blog grid not found.'
          );
        }

        /*
         * Replace article grid
         */
        const currentGrid =
          this.results.querySelector(
            '[data-blog-grid]'
          );

        if (currentGrid) {
          currentGrid.replaceWith(
            newGrid.cloneNode(true)
          );
        }

        /*
         * Replace empty state
         */
        const currentEmpty =
          this.results.querySelector(
            '[data-blog-empty]'
          );

        const newEmpty =
          newResults.querySelector(
            '[data-blog-empty]'
          );

        if (
          currentEmpty &&
          newEmpty
        ) {
          currentEmpty.replaceWith(
            newEmpty.cloneNode(true)
          );
        } else if (
          currentEmpty &&
          !newEmpty
        ) {
          currentEmpty.remove();
        } else if (
          !currentEmpty &&
          newEmpty
        ) {
          this.results.append(
            newEmpty.cloneNode(true)
          );
        }

        /*
         * Replace pagination
         */
        const currentPagination =
          this.root.querySelector(
            '[data-blog-pagination]'
          );

        const newPagination =
          newSection.querySelector(
            '[data-blog-pagination]'
          );

        if (
          currentPagination &&
          newPagination
        ) {
          currentPagination.replaceWith(
            newPagination.cloneNode(true)
          );
        } else if (
          currentPagination &&
          !newPagination
        ) {
          currentPagination.remove();
        } else if (
          !currentPagination &&
          newPagination
        ) {
          this.root.append(
            newPagination.cloneNode(true)
          );
        }

        /*
         * Rebind pagination events
         */
        this.bindPagination();

        /*
         * Update references
         */
        this.grid =
          this.results.querySelector(
            '[data-blog-grid]'
          );

        this.pagination =
          this.root.querySelector(
            '[data-blog-pagination]'
          );

        /*
         * Scroll to top of results
         */
        const rect =
          this.results.getBoundingClientRect();

        const scrollTop =
          window.scrollY +
          rect.top -
          100;

        window.scrollTo({
          top: Math.max(
            scrollTop,
            0
          ),
          behavior: 'smooth',
        });

      } catch (error) {
        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }

        console.error(
          'Blog posts filter error:',
          error
        );
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      if (!this.results) {
        return;
      }

      this.results.classList.toggle(
        'is-loading',
        isLoading
      );

      if (this.nav) {
        this.nav.classList.toggle(
          'is-loading',
          isLoading
        );
      }
    }
  }

  function initBlogPostsFilter(
    root = document
  ) {
    const sections =
      root.querySelectorAll(
        '[data-blog-posts-filter]'
      );

    sections.forEach((section) => {
      if (
        section.dataset
          .blogPostsFilterInitialized ===
        'true'
      ) {
        return;
      }

      section.dataset
        .blogPostsFilterInitialized =
        'true';

      new BlogPostsFilter(section);
    });
  }

  /*
   * Initial load
   */
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initBlogPostsFilter();
      },
      {
        once: true,
      }
    );
  } else {
    initBlogPostsFilter();
  }

  /*
   * Shopify Theme Editor
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initBlogPostsFilter(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        event.target;

      if (
        section.dataset
          .blogPostsFilterInitialized ===
        'true'
      ) {
        delete section.dataset
          .blogPostsFilterInitialized;
      }
    }
  );
})();